// MobileBottomToolbar — floating pill toolbar shown only on phone screens,
// replacing the desktop's fixed left "Layout Stages" sidebar with a row of
// icon+label buttons that open the same content as a bottom sheet instead.
//
// Pattern taken from the reference apps discussed with the client: a
// floating (not edge-docked) rounded pill, icon-over-label buttons (not
// icon-only — the stage names aren't guessable from an icon alone), a
// distinct primary action (Export & Reports) at the end — icon-only, per
// explicit client request, since "Generate Report" as a full text button
// took up too much of the pill's width — safe-area padding for the
// home-indicator on notched phones.
import React from 'react';
import { Ruler, MapPin, Compass as CompassIcon, LayoutGrid, DoorOpen, CalendarDays, FileDown, Settings } from 'lucide-react';

// Maps each STAGES id (see constants/stages.js) to a lucide icon — the
// STAGES constant itself only carries a plain "1".."6" label used for the
// desktop numbered-circle look, so this mapping lives here rather than
// changing that shared constant (which the desktop sidebar still reads
// as-is).
const STAGE_ICONS = {
  unit: Ruler,
  polygon: MapPin,
  compass: CompassIcon,
  'space-analysis': LayoutGrid,
  'entrance-analysis': DoorOpen,
  'astro-numero': CalendarDays,
};

// Explicit per-client-request labels — plain "first word of the desktop
// stage label" (e.g. 'Set Pin / Boundary' -> "Set") wasn't descriptive
// enough on its own icon-sized button on mobile. Only overrides where a
// specific relabel was actually asked for; space-analysis/entrance-analysis
// fall back to their first word same as before.
const STAGE_MOBILE_LABELS = {
  unit: 'Unit',
  polygon: 'Boundary',
  compass: 'Compass',
  'astro-numero': 'Birth',
};

export default function MobileBottomToolbar({ stages, activeSheet, onOpenStage, onOpenMeasurement, onOpenReports, onOpenAccount }) {
  const pillBtn = (isActive) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '3px', minWidth: '52px', padding: '6px 4px', border: 'none', background: 'transparent',
    color: isActive ? '#00A19D' : '#64748b', cursor: 'pointer', flexShrink: 0,
    transition: 'transform 120ms ease, color 120ms ease',
  });

  return (
    <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10100,
      display: 'flex', justifyContent: 'center',
      padding: '0 10px calc(10px + env(safe-area-inset-bottom, 0px))',
      pointerEvents: 'none',
    }}>
      <div className="mobile-toolbar-scroll" style={{
        pointerEvents: 'auto',
        display: 'flex', alignItems: 'center', gap: '2px',
        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(10px)',
        borderRadius: '20px', boxShadow: '0 8px 24px rgba(15,23,42,0.16)', border: '1px solid #EEF1F5',
        padding: '6px 8px', maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch',
      }}>
        {/* Hides the scrollbar track while keeping the row scrollable —
            inline style objects can't target ::-webkit-scrollbar or set
            scrollbar-width, hence the scoped <style> tag below. */}
        <style>{`
          .mobile-toolbar-scroll::-webkit-scrollbar { display: none; }
          .mobile-toolbar-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        `}</style>
        {stages.map((stage) => {
          const Icon = STAGE_ICONS[stage.id] || Ruler;
          const isActive = activeSheet === `stage:${stage.id}`;
          return (
            <button key={stage.id} onClick={() => onOpenStage(stage.id)} style={pillBtn(isActive)}
              onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.93)'; }}
              onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}>
              <Icon size={19} />
              <span style={{ fontSize: '8.5px', fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap' }}>
                {STAGE_MOBILE_LABELS[stage.id] || stage.label.replace(/\*$/, '').split(' ')[0]}
              </span>
            </button>
          );
        })}

        <div style={{ width: '1px', alignSelf: 'stretch', background: '#ECEEF2', margin: '4px 2px' }} />

        <button onClick={onOpenMeasurement} style={pillBtn(activeSheet === 'measurement')}
          onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.93)'; }}
          onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}>
          <Ruler size={19} />
          <span style={{ fontSize: '8.5px', fontWeight: activeSheet === 'measurement' ? 700 : 500 }}>Measure</span>
        </button>

        <button onClick={onOpenReports} aria-label="Export & Reports" title="Export & Reports"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: '6px',
            width: '40px', height: '40px',
            background: '#00A19D', color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer',
          }}
          onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.93)'; }}
          onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}>
          <FileDown size={18} />
        </button>

        {/* Back to Projects and Save moved to the top toolbar row (next to
            Download / after Next in Configure/index.js) per explicit
            request — no longer duplicated here. */}

        {onOpenAccount && (
          <button onClick={onOpenAccount} style={pillBtn(activeSheet === 'account')}
            onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.93)'; }}
            onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}>
            <Settings size={19} />
            <span style={{ fontSize: '8.5px', fontWeight: activeSheet === 'account' ? 700 : 500 }}>Account</span>
          </button>
        )}
      </div>
    </div>
  );
}
